import { Typography, IconButton, Paper, CircularProgress, Alert } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { ArrowBack, Delete } from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import usePageTitle from "../../../Common/hooks/usePageTitle";
import TopBar from "../../../Layout/components/TopBar/TopBar";
import AddPackageMemberSection from "../components/AddPackageMemberSection";
import { getPackageMembers, addPackageMember, removePackageMember, getPackageUserRoles } from "../api/adminPackageClient";
import { PackageMemberDto, UserPackagePermissions } from "../api/models/PackageDto";
import styles from "./PackageAccessView.module.scss";

const PackageAccessView = (): JSX.Element => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    usePageTitle("Admin | Manage Package Access");

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [members, setMembers] = useState<PackageMemberDto[]>([]);
    const [permissions, setPermissions] = useState<UserPackagePermissions | null>(null);

    useEffect(() => {
        loadData();
    }, [id]);

    const loadData = async () => {
        if (!id) return;

        try {
            setLoading(true);
            setError(null);
            const [membersData, perms] = await Promise.all([
                getPackageMembers(id),
                getPackageUserRoles(id)
            ]);
            setMembers(membersData);
            setPermissions(perms);
        } catch (err: any) {
            setError(err?.response?.data || "Failed to load package members");
        } finally {
            setLoading(false);
        }
    };

    const handleAddMember = async (name: string, email: string, adPrinciple: string, role: "Owner" | "Editor") => {
        if (!id || !permissions) return;

        try {
            setSaving(true);
            setError(null);
            setSuccess(null);

            await addPackageMember({
                packageId: id,
                memberName: name,
                memberEmail: email,
                memberAdPrinciple: adPrinciple,
                role: role
            });

            setSuccess(`Successfully added ${name} as ${role}`);
            await loadData();
        } catch (err: any) {
            setError(err?.response?.data || "Failed to add member");
        } finally {
            setSaving(false);
        }
    };

    const handleRemoveMember = async (member: PackageMemberDto) => {
        if (!id || !permissions) return;

        // Only admins can remove owners
        if (member.role === "Owner" && !permissions.isAdmin) {
            setError("Only administrators can remove owners");
            return;
        }

        if (!window.confirm(`Are you sure you want to remove ${member.memberName} as ${member.role}?`)) {
            return;
        }

        try {
            setSaving(true);
            setError(null);
            setSuccess(null);

            await removePackageMember({
                packageId: id,
                memberId: member.memberId,
                role: member.role
            });

            setSuccess(`Successfully removed ${member.memberName}`);
            await loadData();
        } catch (err: any) {
            setError(err?.response?.data || "Failed to remove member");
        } finally {
            setSaving(false);
        }
    };

    const handleBack = () => {
        navigate("/admin/packages");
    };

    if (!id) {
        return <div>Invalid package ID</div>;
    }

    if (loading) {
        return (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                <CircularProgress />
            </div>
        );
    }

    if (!permissions?.canManageAccess) {
        return (
            <div>
                <TopBar>
                    <div className={styles.details__topBarContainer}>
                        <IconButton onClick={handleBack} sx={{ mr: 2 }}>
                            <ArrowBack />
                        </IconButton>
                        <Typography variant="h4">Manage Package Access</Typography>
                    </div>
                </TopBar>
                <div style={{ padding: "24px" }}>
                    <Alert severity="error">
                        You don't have permission to manage access for this package.
                    </Alert>
                </div>
            </div>
        );
    }

    const columns: GridColDef[] = [
        {
            field: "memberName",
            headerName: "Name",
            flex: 1,
        },
        {
            field: "memberEmail",
            headerName: "Email",
            flex: 1,
        },
        {
            field: "role",
            headerName: "Role",
            flex: 0.5,
        },
        {
            field: "addedOn",
            headerName: "Added On",
            flex: 0.7,
            renderCell: (params) => {
                return params.value ? new Date(params.value as string).toLocaleDateString() : "-";
            }
        },
        {
            field: "actions",
            headerName: "Actions",
            flex: 0.5,
            sortable: false,
            renderCell: (params) => {
                const member = params.row as PackageMemberDto;
                const canRemove = permissions?.isAdmin || (permissions?.isOwner && member.role !== "Owner");

                return canRemove ? (
                    <IconButton
                        onClick={() => handleRemoveMember(member)}
                        disabled={saving}
                        color="error"
                        size="small"
                    >
                        <Delete />
                    </IconButton>
                ) : null;
            }
        }
    ];

    const rows = members.map((member, index) => ({
        id: `${member.memberId}-${member.role}`,
        ...member
    }));

    return (
        <div id="packageaccess" data-testid="packageaccess" style={{ height: "100%" }}>
            <TopBar>
                <div className={styles.details__topBarContainer}>
                    <IconButton onClick={handleBack} sx={{ mr: 2 }}>
                        <ArrowBack />
                    </IconButton>
                    <Typography variant="h4">Manage Package Access</Typography>
                </div>
            </TopBar>

            <div style={{ padding: "24px" }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {success && (
                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                        {success}
                    </Alert>
                )}

                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        Add Owner or Editor
                    </Typography>
                    <AddPackageMemberSection
                        onAddMember={handleAddMember}
                        existingMembers={members.map(m => ({
                            email: m.memberEmail,
                            adPrinciple: m.memberEmail
                        }))}
                    />
                </Paper>

                <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        Current Members
                    </Typography>
                    <DataGrid
                        columns={columns}
                        rows={rows}
                        loading={saving}
                        autoHeight
                        pageSize={10}
                        rowsPerPageOptions={[10, 25, 50]}
                        disableSelectionOnClick
                    />
                </Paper>
            </div>
        </div>
    );
};

export default PackageAccessView;
